// External packages
import { zodResolver } from '@hookform/resolvers/zod';
import * as ReactAria from 'react-aria-components';
import * as ReactHookForm from 'react-hook-form';
import * as React from 'react';
import { z } from 'zod';

// Components
import * as Forms from 'components/Forms';

// Utils
import { twMerge } from 'tailwind-merge';

export type FormProps<T extends z.ZodType<any, any>> =
  ReactHookForm.UseFormProps<z.infer<T>> & {
    schema: T;
    onSubmit: (
      values: z.infer<T>,
      form: ReactHookForm.UseFormReturn<z.infer<T>>
    ) => void | Promise<void>;
    defaultValues?: ReactHookForm.DefaultValues<z.infer<T>>;
    forceValues?: z.infer<T>;
    children?: React.ReactNode;
    formProps?: Omit<React.ComponentProps<'form'>, 'onSubmit'>;
    className?: string;
  };

export const Form = <T extends z.ZodType<any, any>>({
  schema,
  onSubmit,
  children,
  forceValues,
  formProps,
  ...props
}: FormProps<T>) => {
  const form = ReactHookForm.useForm({
    resolver: zodResolver(schema),
    ...props,
  });

  const submitHandler = React.useCallback(
    (values: z.infer<T>) => {
      return onSubmit(values, form);
    },
    [onSubmit, form]
  );

  const onFormSubmit: React.FormEventHandler<HTMLFormElement> =
    React.useCallback(
      (event) => {
        event.preventDefault();
        event.stopPropagation();
        form.handleSubmit(submitHandler)(event);
      },
      [form, submitHandler]
    );

  React.useEffect(() => {
    if (forceValues) {
      form.reset(forceValues);
    }
  }, [forceValues]);

  return (
    <ReactHookForm.FormProvider {...form}>
      <form {...formProps} onSubmit={onFormSubmit}>
        <fieldset {...props} disabled={form.formState.isSubmitting}>
          {children}
        </fieldset>
      </form>
    </ReactHookForm.FormProvider>
  );
};

export type InputFieldProps = {
  name: string;
  label?: string;
  labelProps?: Forms.LabelProps;
  inputProps?: Forms.InputProps;
  subLabelProps?: Forms.SubLabelProps;
} & ReactAria.TextFieldProps;

export const InputField: React.FC<InputFieldProps> = ({
  name,
  label,
  labelProps,
  inputProps,
  subLabelProps,
  isInvalid,
  defaultValue,
  className,
  ...props
}) => {
  const { field, fieldState } = ReactHookForm.useController<
    { __name__: string },
    '__name__'
  >({ name: name as '__name__', defaultValue });
  return (
    <Forms.TextField
      {...props}
      {...field}
      id={name}
      value={field.value ?? ''}
      isInvalid={Boolean(fieldState.error) ?? isInvalid}
      className={twMerge('group', className as string)}
    >
      {label && (
        <Forms.Label {...labelProps} htmlFor={name}>
          {label}
        </Forms.Label>
      )}
      {props.type === 'password' ? (
        <Forms.InputPassword {...inputProps} />
      ) : (
        <Forms.Input {...inputProps} />
      )}
      {fieldState.error && (
        <Forms.SubLabel
          {...subLabelProps}
          role="alert"
          slot="errorMessage"
          colorScheme="error"
        >
          {fieldState.error.message}
        </Forms.SubLabel>
      )}
    </Forms.TextField>
  );
};

export type TextAreaFieldProps = {
  name: string;
  label?: string;
  labelProps?: Forms.LabelProps;
  textAreaProps?: Forms.TextAreaProps;
  subLabelProps?: Forms.SubLabelProps;
} & ReactAria.TextFieldProps;

export const TextAreaField: React.FC<TextAreaFieldProps> = ({
  name,
  label,
  labelProps,
  textAreaProps,
  subLabelProps,
  isInvalid,
  defaultValue,
  className,
  ...props
}) => {
  const { field, fieldState } = ReactHookForm.useController<
    { __name__: string },
    '__name__'
  >({ name: name as '__name__', defaultValue });
  return (
    <Forms.TextField
      {...props}
      {...field}
      id={name}
      value={field.value ?? ''}
      isInvalid={Boolean(fieldState.error) ?? isInvalid}
      className={twMerge('group', className as string)}
    >
      {label && (
        <Forms.Label {...labelProps} htmlFor={name}>
          {label}
        </Forms.Label>
      )}
      <Forms.TextArea {...textAreaProps} />
      {fieldState.error && (
        <Forms.SubLabel
          {...subLabelProps}
          role="alert"
          slot="errorMessage"
          colorScheme="error"
        >
          {fieldState.error.message}
        </Forms.SubLabel>
      )}
    </Forms.TextField>
  );
};
